<template>
    <div>
        <div class="body-content">
            <div class="page-header">
                <h1>Missing Shipping Bill from Container</h1>
            </div>
            
            <div class="form-group">
                <div class="form-item-full">
                    <label for="containerList">Container Numbers *</label>
                    <textarea
                        id="containerList"
                        v-model="containerNumbers"
                        placeholder="Enter container numbers (one per line or comma-separated)"
                        rows="8"
                        class="container-textarea"
                    ></textarea>
                    <p class="help-text">Enter container numbers one per line or separated by commas</p>
                </div>
            </div>

            <div class="button-group">
                <button
                    type="button"
                    class="excel-button"
                    @click="downloadExcel"
                    :disabled="isSubmitting || !containerNumbers.trim()"
                >
                    <i class="fas fa-download"></i> Download Excel
                </button>
                <button
                    type="button"
                    class="cancel-button"
                    @click="clearForm"
                    :disabled="isSubmitting"
                >
                    Clear
                </button>
            </div>

            <div v-if="message" class="message" :class="messageType">
                {{ message }}
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
    name: 'MissingShippingBillFromContainer',
    data() {
        return {
            containerNumbers: '',
            isSubmitting: false,
            message: '',
            messageType: 'info',
        };
    },
    methods: {
        async downloadExcel() {
            if (this.isSubmitting) return;
            const containers = this.containerNumbers
                .split(/[\n,]+/)
                .map(num => num.trim())
                .filter(num => num !== '');
            if (containers.length === 0) {
                useToast().error('Please enter at least one container number.');
                return;
            }

            this.isSubmitting = true;
            this.message = '';

            try {
                const token = localStorage.getItem('access_token');
                if (!token) {
                    useToast().error('Authentication token not found.');
                    this.isSubmitting = false;
                    return;
                }

                const config = {
                    headers: {
                        'accept': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    responseType: 'blob',
                };
                const payload=
                {
                    contnumber:this.containerNumbers
                }
                const data = new FormData();
                data.append('transaction', JSON.stringify(payload));
                const response = await axios.post('/missingshippingbilldata', data, config);

                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute(
                    'download',
                    `missing_sb_${new Date().getTime()}.xlsx`
                );
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);

                this.message = `Successfully downloaded Excel for ${containers.length} container(s).`;
                this.messageType = 'success';
                useToast().success(`Excel file downloaded for ${containers.length} container(s).`);
            } catch (error) {
                console.error('Error downloading Excel:', error);
                const errorMessage = error.response?.data?.detail || 
                    error.response?.data?.message || 
                    'Error downloading Excel file.';
                this.message = errorMessage;
                this.messageType = 'error';
                useToast().error(errorMessage);
            } finally {
                this.isSubmitting = false;
            }
        },
        clearForm() {
            this.containerNumbers = '';
            this.message = '';
            this.messageType = 'info';
        },
    },
};
</script>

<style scoped>
.body-content {
    background-color: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.page-header {
    margin-bottom: 30px;
}

.page-header h1 {
    margin: 0;
    font-size: 1.8rem;
    color: #333;
    border-bottom: 2px solid #17a2b8;
    padding-bottom: 10px;
}

.form-group {
    margin-bottom: 20px;
}

.form-item-full {
    width: 100%;
}

.form-item-full label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;
}

.container-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: monospace;
    font-size: 14px;
    resize: vertical;
    box-sizing: border-box;
}

.container-textarea:focus {
    outline: none;
    border-color: #17a2b8;
    box-shadow: 0 0 0 3px rgba(23, 162, 184, 0.1);
}

.help-text {
    margin-top: 8px;
    font-size: 0.9rem;
    color: #666;
    font-style: italic;
}

.button-group {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
}

.excel-button {
    background-color: #17a2b8;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    transition: background-color 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
}

.excel-button:hover:not(:disabled) {
    background-color: #138496;
}

.excel-button:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
}

.cancel-button {
    background-color: #6c757d;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    transition: background-color 0.3s ease;
}

.cancel-button:hover:not(:disabled) {
    background-color: #5a6268;
}

.cancel-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.message {
    padding: 12px 16px;
    border-radius: 4px;
    margin-top: 20px;
    font-weight: 500;
}

.message.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.message.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.message.info {
    background-color: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
}

@media (max-width: 768px) {
    .body-content {
        padding: 15px;
    }

    .page-header h1 {
        font-size: 1.5rem;
    }

    .button-group {
        flex-wrap: wrap;
    }

    .excel-button,
    .cancel-button {
        flex: 1;
        min-width: 150px;
    }

    .container-textarea {
        font-size: 12px;
    }
}
</style>
